<template>
    <div class="container p-12 colo-order">
        <h2>Add Colocation</h2>

        <div class="flex flex-wrap">
            <div class="w-full pb-8 md:w-2/3 md:pr-4">
                <ul class="breadcrumb bg-grey-100 rounded p-4 mb-8">
                    <li class="breadcrumb-item">
                        <router-link to="/order">Order</router-link>
                    </li>
                    <li class="breadcrumb-item active">Colocation</li>
                </ul>

                <div class="row">
                    <div class="col-12">
                        <h3 class="mb-6">
                            <span class="text-green">Step 1 - </span>
                            <span class="text-blue">Select a Data Centre</span>
                        </h3>
                    </div>
                    <div class="col">
                        <div class="location-grid mb-4">
                            <div
                                v-for="(location, index) in config"
                                :key="index"
                                @click="selectAllocation(index)"
                                class="sau-bx"
                                :class="{ 'border-success': index == allocationSelected }"
                            >
                                <img :src="locationImage(location.label)" :alt="location.title">
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12 text-center py-12 my-12" v-if="!allocationReady">
                        <img src="/img/spinner.gif" />
                    </div>

                    <template v-else>
                        <div class="col-12">
                            <h3 class="mb-6 mt-12">
                                <span class="text-green">Step 2 - </span>
                                <span class="text-blue">Select an Allocation</span>
                            </h3>
                        </div>

                        <div class="col-md-6 col-lg-4" v-for="ru in allocations" :key="ru.id">
                            <div class="sau-product-card pb-4">
                                <label :for="ru.id">
                                    <div class="sau-product-card-details">
                                        <header>
                                            <i class="icon icon-sau-colocation" aria-hidden="true"></i>
                                        </header>
                                        <ul>
                                            <li class="sub-head">
                                                <p class="list-title">{{ ru.title }}</p>
                                            </li>
                                            <li class="px-2">
                                                <p class="list-desc">
                                                    <span class="text-dark sau-font-bold">IP's:</span>
                                                    {{ ru.ips }}
                                                </p>
                                            </li>
                                            <li class="px-2">
                                                <p class="list-desc">
                                                    <span
                                                        class="text-dark sau-font-bold"
                                                    >Network Ports:</span>
                                                    {{ ru.networkports }}
                                                </p>
                                            </li>
                                            <li class="px-2">
                                                <p class="list-desc">
                                                    <span class="text-dark sau-font-bold">PDU's:</span>
                                                    {{ ru.pdu }}
                                                </p>
                                            </li>
                                            <li class="px-2">
                                                <p class="list-desc">
                                                    <span class="text-dark sau-font-bold">Data:</span>
                                                    {{ ru.data }}
                                                </p>
                                            </li>
                                            <li class="px-2">
                                                <p class="list-desc">
                                                    <span class="text-dark sau-font-bold">Access:</span>
                                                    {{ ru.access }}
                                                </p>
                                            </li>
                                            <li class="px-2">
                                                <p class="list-desc">
                                                    <span class="text-dark sau-font-bold">kW:</span>
                                                    {{ ru.kw }}
                                                </p>
                                            </li>
                                        </ul>
                                        <div class="price-box-sm">
                                            <p class="price">{{ ru.recurring | currency }}</p>
                                            <p class="pm">Per Month Ex. GST</p>
                                        </div>
                                        <div class="allocation-buy mt-4">
                                            <template v-if="!ru.inOrder">
                                                <button
                                                    class="btn btn-success w-100"
                                                    @click="addColo(ru)"
                                                    :disabled="ru.processing"
                                                >Add to order</button>
                                            </template>
                                            <template v-else>
                                                <button
                                                    class="btn btn-primary w-100"
                                                    @click="removeColo(ru.id)"
                                                    :disabled="ru.processing"
                                                >Remove from order</button>
                                            </template>
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </template>
                </div>
            </div>

            <sau-ordering-sidebar :data="data" :order="order" :processing="false"></sau-ordering-sidebar>
        </div>
    </div>
</template>

<script>
import Helpers from '@js/helpers';
import OrderSummary from './Summary';

export default {
    props: {
        data: Object,
        order: Object
    },

    components: {
        OrderSummary
    },

    data() {
        return {
            config: {},
            colocation: {},
            type: 'colocation',
            slug: null,
            allocationSelected: 0,
            colocation: null,
            allocationReady: true,
            sidebarWidth: '100%',
            sidebarHeight: 'auto'
        };
    },

    mounted() {
        // pluck out and colos in the order
        this.colocation = this.order.items.filter(
            item => item.type === this.type
        );

        // add the 'inOrder' property to each of the RU's
        this.config = this.data.config.products.colocation.map(colo => {
            colo.rus = colo.rus.map(ru => {
                ru.inOrder = false;
                // Check to see if the colo is already in the order
                if (
                    this.colocation.find(item => item.allocation.id === ru.id)
                ) {
                    ru.inOrder = true;
                }
                return ru;
            });

            return colo;
        });

        if (this.data.active.data_centre) {
            this.config.map((allocation, key) => {
                if (allocation.id === this.data.active.data_centre) {
                    this.selectAllocation(key);
                }
            });
        }
    },

    methods: {
        locationImage(location) {
            return `/img/data-centre-${location}.gif`;
        },

        selectAllocation(index) {
            // A short timeout to visually indicate that the options have changed
            this.allocationReady = false;
            this.allocationSelected = index;

            setTimeout(() => {
                this.allocationReady = true;
            }, 250);
        },

        // Generic function to toggle properties on the individual RU's
        toggleAllocationProperty(property, id) {
            this.config = this.config.map(location => {
                location.rus = location.rus.map(ru => {
                    if (ru.id === id) {
                        ru[property] = !ru[property];
                    }
                    return ru;
                });
                return location;
            });
        },

        addColo(allocation) {
            let locationId = this.config[this.allocationSelected].id;

            this.toggleAllocationProperty('inOrder', allocation.id);
            this.toggleAllocationProperty('processing', allocation.id);

            let itemData = {
                location: locationId,
                data_centre_allocation_id: allocation.id,
                allocation: {
                    id: allocation.id // This is here because we use the selected allocations in a computed property and it must be defined
                }
            };

            this.$parent
                .addOrderItemPromise(this.type, itemData)
                .then(response => {
                    // Set the item data (location, ru id)

                    this.$parent
                        .updateItemPromise(this.type, response.slug, itemData)
                        .then(response => {
                            // Toggle the state (for disabling ui)
                            this.toggleAllocationProperty(
                                'processing',
                                allocation.id
                            );
                        });
                });
        },

        removeColo(allocationId) {
            this.toggleAllocationProperty('inOrder', allocationId);
            this.toggleAllocationProperty('processing', allocationId);

            let itemToRemove = this.order.items.find(
                item => item.allocation.id === allocationId
            );

            this.$parent.removeItemPromise(itemToRemove).then(response => {
                this.toggleAllocationProperty('processing', allocationId);
            });
        }
    },

    computed: {
        allocations() {
            if (this.config && this.config[this.allocationSelected]) {
                // Check if the RU/item exists in the order - make this computed so if an item is removed from another component (summary) it will still update
                this.config = this.config.map(location => {
                    location.rus = location.rus.map(ru => {
                        if (
                            this.colocationInOrder.find(
                                item => item.allocation.id === ru.id
                            )
                        ) {
                            ru.inOrder = true;
                        } else {
                            ru.inOrder = false;
                        }
                        return ru;
                    });
                    return location;
                });
                return this.config
                    ? this.config[this.allocationSelected].rus
                    : null;
            }
        },

        colocationInOrder() {
            return this.order.items.filter(item => item.type === this.type);
        }
    }
};
</script>
<style lang="scss" scoped>
.price-box-sm {
    background-color: #fff;
}
.input-card-label label:hover,
.sau-product-card label:hover,
.sau-product-card-button label:hover {
    border: 2px solid #e5e5e5;
    box-shadow: none;
}
</style>
